import xCircle from '@iconify/icons-heroicons-solid/x-circle';
import icon3DotsScaleMiddle from '@iconify/icons-svg-spinners/3-dots-scale-middle';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { BFILogoHorizontal } from '@assets/images/svg';
import MemoizedIcon from '@components/MemoizedIcon';
import { PlaceholderView } from '@components/PlaceholderView';
import { EventName } from '@customTypes/Analytics';
import { ProductId, RolesWithProduct } from '@customTypes/api/Commons';
import { SpvDownline } from '@customTypes/Commons';
import useGetEmployeeDetail from '@hooks/reactQueryHooks/employeeDetail/useGetEmployeeDetail';
import useGetIAMAssignedPermission from '@hooks/reactQueryHooks/getIAMAssignedPermission';
import useDatadogTrackerEffect from '@hooks/useDatadogTrackerEffect';
import { logEvent, setUserId, setUserProperties } from '@service/firebase';
import { SPV_DOWNLINE_BY_PRODUCT_ID } from '@src/constants';
import { useAuthenticationContext } from '@src/context/AuthenticationContext';
import { getClientInfo } from '@src/utils/clientDetector';
import { parseValue } from '@src/utils/utils';

import { AUTHORIZED_ROLES } from './constants';
import { Roles } from './types';

interface AuthorizationContextProps {
  isAllowed: (name: string, rule?: string) => boolean;
  emp_no: string;
  emp_name: string;
  corporate_email: string;
  marketing_id: string;
  job_title_name: string;
  isLoading: boolean;
  rolesProduct: RolesWithProduct;
  spvDownlineName: string;
}

const AuthorizationContext = createContext<AuthorizationContextProps>({
  isAllowed: (_name: string, _rule?: string) => false,
  emp_no: '',
  emp_name: '',
  corporate_email: '',
  marketing_id: '',
  job_title_name: '',
  isLoading: false,
  rolesProduct: RolesWithProduct.None,
  spvDownlineName: SpvDownline.are,
});

const AuthorizationContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();

  const { doLogout } = useAuthenticationContext();
  const { data: permissionData, isLoading: isPermissionDataLoading } =
    useGetIAMAssignedPermission();
  const { data: employeeDetail, isLoading: isEmployeeDetailLoading } =
    useGetEmployeeDetail(!isPermissionDataLoading);

  const isAuthorized =
    permissionData?.role &&
    Object.keys(permissionData.role).some(roleKey =>
      AUTHORIZED_ROLES.includes(roleKey as Roles),
    );

  const roles =
    (permissionData?.role &&
      (Object.keys(permissionData.role).find(roleKey =>
        AUTHORIZED_ROLES.includes(roleKey as Roles),
      ) as Roles)) ??
    Roles.None;

  const isAllowed = useCallback(
    (name: string, type = 'view') =>
      !!permissionData?.permission &&
      !!Object.keys(permissionData.permission).find(rule => {
        const ruleKeys = rule.replace('agency.spvcockpit.', '').split('.');
        const ruleType = ruleKeys.pop();
        return ruleKeys.join('.') === name && ruleType === type;
      }),
    [permissionData?.permission],
  );

  const isLoading = isEmployeeDetailLoading || isPermissionDataLoading;

  const productId = parseValue(employeeDetail?.product_id, '');

  const spvDownlineName = useMemo(
    () =>
      parseValue(
        SPV_DOWNLINE_BY_PRODUCT_ID[
          [roles, productId].filter(Boolean).join('.') as RolesWithProduct
        ],
        SpvDownline.are,
      ),
    [productId, roles],
  );

  useDatadogTrackerEffect(employeeDetail);

  useEffect(() => {
    if (employeeDetail) {
      const clientData = getClientInfo();
      setUserId(employeeDetail?.emp_no);
      setUserProperties({
        name: employeeDetail?.emp_name,
        role: roles.replace('agency.spvcockpit.', ''),
        email: employeeDetail?.corporate_email,
        marketing_id: employeeDetail?.marketing_id,
        product: employeeDetail?.product_id,
        branch_code: employeeDetail?.branch_id,
        device_os: clientData.os,

        browser_type: clientData.browser,
        device_type: clientData.deviceType,
      });
      logEvent(EventName.UserLogin);
    }
  }, [employeeDetail, roles]);

  const contextValue = useMemo(() => {
    const product_id = ![Roles.Abpm, Roles.Bm].includes(roles)
      ? parseValue(employeeDetail?.product_id, ProductId.None)
      : ProductId.None;

    return {
      emp_no: parseValue(employeeDetail?.emp_no, ''),
      emp_name: parseValue(employeeDetail?.emp_name, ''),
      corporate_email: parseValue(employeeDetail?.corporate_email, ''),
      marketing_id: parseValue(employeeDetail?.marketing_id, ''),
      job_title_name: parseValue(employeeDetail?.job_title_name, ''),
      job_title_code: parseValue(employeeDetail?.job_title_code, ''),
      isLoading,
      rolesProduct: [roles, product_id]
        .filter(Boolean)
        .join('.') as RolesWithProduct,
      isAllowed,
      spvDownlineName,
    };
  }, [
    roles,
    employeeDetail?.product_id,
    employeeDetail?.emp_no,
    employeeDetail?.emp_name,
    employeeDetail?.corporate_email,
    employeeDetail?.marketing_id,
    employeeDetail?.job_title_name,
    employeeDetail?.job_title_code,
    isLoading,
    isAllowed,
    spvDownlineName,
  ]);

  return (
    <AuthorizationContext.Provider value={contextValue}>
      {isAuthorized && !isLoading ? (
        children
      ) : (
        <div className="flex flex-col w-full h-screen">
          <div className="bg-white sm:rounded-none h-[67px] sm:h-[77px] fixed w-full z-50 shadow-[0_1px_4px_rgba(0,0,0,0.08)]">
            <img
              src={BFILogoHorizontal}
              alt="BFILogoHorizontal"
              width={123}
              height={35}
              className="h-full m-auto"
            />
          </div>
          <div className="flex flex-col h-full pt-[67px] sm:pt-[77px]">
            <div className="flex flex-col h-full px-5 sm:px-0 sm:mx-10 sm:mt-[69px] sm:rounded-t-lg bg-white items-center">
              {isLoading ? (
                <>
                  <MemoizedIcon
                    icon={icon3DotsScaleMiddle}
                    className="text-primary-500 text-[80px] mt-auto"
                  />
                  <div className="text-lg mt-8 mb-auto text-center">
                    {t('authGate.redirecting')}
                  </div>
                </>
              ) : (
                <PlaceholderView
                  icon={xCircle}
                  iconContainerClassName="bg-danger-50 text-danger-500"
                  mainText={t('authGate.unauthorizedMainText')}
                  subText={t('authGate.unauthorizedSubText')}
                  actionText={t('authGate.loginWithOtherAccount')}
                  action={doLogout}
                  containerClassName="m-auto"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </AuthorizationContext.Provider>
  );
};

const useAuthorizationContext = () => useContext(AuthorizationContext);

export {
  AuthorizationContext,
  AuthorizationContextProvider,
  useAuthorizationContext,
};
